import { useTranslation } from 'next-i18next'
import React from 'react'

import { BeltVisual } from '@goodfellas/belt-visual'
import * as Belt from '@goodfellas/graduation'

import editFighterInfo from '../../../functions/editFighterInfo'
import { useStore, useTableStore } from '../../../functions/store'
import { FighterType } from '../../../types/fighter'
import GraduationModal from '../graduation/GraduationModal'
import EditButton from './editButton'
import SaveButton from './saveButton'

interface FighterRowConfirmationTableProps {
  phone: string
  name: string
  belt: string
  beltDegree: number
  type: string
}

const FighterRowConfirmationTable: React.FC<
  FighterRowConfirmationTableProps
> = ({ phone, name, belt, beltDegree, type }) => {
  const { t } = useTranslation('avaliacao360')
  const { updateFighterData } = useTableStore()
  const userStore = useStore((s) => s)
  const [isGradOpen, setIsGradOpen] = React.useState(false)
  const {
    isEditingFighter,
    fighterName,
    fighterBelt,
    fighterBeltDegree,
    fighterType,
    handleNameChange,
    handleBeltChange,
    handleBeltDegreeChange,
    handleTypeChange,
    handleEditFighterClick,
    handleSaveFighterClick,
  } = editFighterInfo(phone, name, belt, beltDegree, type, updateFighterData)

  const resolvedBelt: Belt.Colors = (
    Object.values(Belt.Colors) as string[]
  ).includes(belt)
    ? (belt as Belt.Colors)
    : Belt.Colors.White

  const isInstructor =
    fighterBelt === Belt.Colors.Brown ||
    fighterBelt === Belt.Colors.Black ||
    fighterBelt === Belt.Colors.RedBlack ||
    fighterBelt === Belt.Colors.RedWhite ||
    fighterBelt === Belt.Colors.Red

  const isMaster =
    fighterBelt === Belt.Colors.RedBlack ||
    fighterBelt === Belt.Colors.RedWhite ||
    fighterBelt === Belt.Colors.Red

  const groupId = userStore.group?._id ? String(userStore.group._id) : ''

  const canEditBelt =
    userStore.type === FighterType.HEAD_COACH ||
    userStore.type === FighterType.PROFESSOR

  return (
    <div
      className={`
        group transition-all duration-200
        ${isEditingFighter ? 'bg-slate-700/50' : 'hover:bg-slate-700/30'}
      `}
    >
      <GraduationModal
        isOpen={isGradOpen}
        phoneNumber={phone}
        groupId={groupId}
        initialName={name}
        initialType={type}
        initialBelt={resolvedBelt}
        initialDegree={beltDegree || 0}
        onClose={() => setIsGradOpen(false)}
        onSaved={({ belt: nextBelt, degree: nextDegree }) => {
          updateFighterData(phone, name, nextBelt, nextDegree, type)
        }}
      />

      {/* Desktop Layout */}
      <div className="hidden sm:grid grid-cols-[1fr_2fr_1fr_0.5fr_1fr_0.5fr] gap-2 px-4 py-3 items-center">
        {/* Phone */}
        <div className="text-sm text-slate-300 font-mono truncate">
          +{phone}
        </div>

        {/* Name */}
        {!isEditingFighter ? (
          <div className="text-sm text-white font-medium truncate">
            {name || t('table.notAvailable')}
          </div>
        ) : (
          <input
            type="text"
            value={fighterName}
            onChange={handleNameChange}
            className="px-3 py-1.5 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-red-500/50 w-full"
          />
        )}

        {/* Belt */}
        {!isEditingFighter ? (
          <div className="flex items-center">
            {/* Always show a belt visual; if missing/invalid belt in DB, fallback to White */}
            <button
              type="button"
              onClick={() => setIsGradOpen(true)}
              className="rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/40 hover:opacity-95 transition-opacity disabled:cursor-not-allowed"
              title="Editar graduação"
              disabled={!groupId || !canEditBelt}
            >
              <BeltVisual
                color={resolvedBelt}
                degree={beltDegree || 0}
                size="sm"
                className="origin-left scale-90 sm:scale-100"
              />
            </button>
          </div>
        ) : (
          <select
            value={fighterBelt}
            onChange={handleBeltChange}
            disabled={!canEditBelt}
            className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-red-500/50 w-full disabled:cursor-not-allowed"
          >
            {!fighterBelt && (
              <option disabled value="">
                {t('table.notAvailable')}
              </option>
            )}
            <option value={Belt.Colors.Gray}>⚪ {t('belts.gray')}</option>
            <option value={Belt.Colors.Yellow}>🟡 {t('belts.yellow')}</option>
            <option value={Belt.Colors.Orange}>🟠 {t('belts.orange')}</option>
            <option value={Belt.Colors.Green}>🟢 {t('belts.green')}</option>
            <option value={Belt.Colors.White}>⚪ {t('belts.white')}</option>
            <option value={Belt.Colors.Blue}>🔵 {t('belts.blue')}</option>
            <option value={Belt.Colors.Purple}>🟣 {t('belts.purple')}</option>
            <option value={Belt.Colors.Brown}>🟤 {t('belts.brown')}</option>
            <option value={Belt.Colors.Black}>⚫ {t('belts.black')}</option>
            <option value={Belt.Colors.RedBlack}>
              🔴⚫ {t('belts.redBlack')}
            </option>
            <option value={Belt.Colors.RedWhite}>
              🔴⚪ {t('belts.redWhite')}
            </option>
            <option value={Belt.Colors.Red}>🔴 {t('belts.red')}</option>
          </select>
        )}

        {/* Degree */}
        {!isEditingFighter ? (
          <div className="text-sm text-slate-300 text-center">
            {beltDegree || 0}
          </div>
        ) : (
          <select
            value={fighterBeltDegree}
            onChange={handleBeltDegreeChange}
            disabled={!canEditBelt}
            className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-red-500/50 w-full text-center disabled:cursor-not-allowed"
          >
            <option value={0}>0</option>
            <option value={1}>1</option>
            <option value={2}>2</option>
            <option value={3}>3</option>
            <option value={4}>4</option>
            {fighterBelt === Belt.Colors.Black && <option value={5}>5</option>}
            {fighterBelt === Belt.Colors.Black && <option value={6}>6</option>}
            {fighterBelt === Belt.Colors.RedBlack && (
              <option value={7}>7</option>
            )}
            {fighterBelt === Belt.Colors.RedWhite && (
              <option value={8}>8</option>
            )}
            {fighterBelt === Belt.Colors.Red && <option value={9}>9</option>}
          </select>
        )}

        {/* Type/Role */}
        {!isEditingFighter ? (
          <div className="text-sm text-slate-300 truncate">
            {type ? t(`table.types.${type}`) : t('table.notAvailable')}
          </div>
        ) : (
          <select
            value={fighterType}
            onChange={handleTypeChange}
            className="px-2 py-1.5 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-red-500/50 w-full"
          >
            {!fighterType && (
              <option disabled value="">
                {t('table.notAvailable')}
              </option>
            )}
            <option value={FighterType.STUDENT}>
              {t(`table.types.${FighterType.STUDENT}`)}
            </option>
            {(isInstructor || isMaster) && (
              <option value={FighterType.HEAD_COACH}>
                {t(`table.types.${FighterType.HEAD_COACH}`)}
              </option>
            )}
            {(isInstructor || isMaster) && (
              <option value={FighterType.PROFESSOR}>
                {t(`table.types.${FighterType.PROFESSOR}`)}
              </option>
            )}
            {isMaster && (
              <option value={FighterType.MASTER}>
                {t(`table.types.${FighterType.MASTER}`)}
              </option>
            )}
            <option value={FighterType.OTHER}>
              {t(`table.types.${FighterType.OTHER}`)}
            </option>
          </select>
        )}

        {/* Edit/Save Button */}
        <div className="flex justify-center">
          {!isEditingFighter ? (
            <EditButton onClick={handleEditFighterClick} size={18} />
          ) : (
            <SaveButton onClick={handleSaveFighterClick} size="tiny" />
          )}
        </div>
      </div>

      {/* Mobile Layout - Beautiful Card Style */}
      <div className="sm:hidden">
        <div
          className={`
            mx-2 my-2 p-4 rounded-xl border transition-all duration-200
            ${
              isEditingFighter
                ? 'bg-slate-700/60 border-red-500/30'
                : 'bg-gradient-to-br from-slate-800/80 to-slate-900/60 border-white/5 hover:border-white/10'
            }
          `}
        >
          {/* Card Header - Name & Edit Button */}
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="flex-1 min-w-0">
                {!isEditingFighter ? (
                  <>
                    <h3 className="text-base font-semibold text-white truncate">
                      {name || t('table.notAvailable')}
                    </h3>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <svg
                        className="w-3.5 h-3.5 text-slate-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                      <span className="text-xs text-slate-400 font-mono">
                        +{phone}
                      </span>
                    </div>
                  </>
                ) : (
                  <input
                    type="text"
                    value={fighterName}
                    onChange={handleNameChange}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-red-500/50"
                    placeholder={t('table.labels.name')?.toString()}
                  />
                )}
              </div>
            </div>

            {/* Edit/Save Button */}
            <div className="flex-shrink-0">
              {!isEditingFighter ? (
                <button
                  onClick={handleEditFighterClick}
                  className="p-2 rounded-lg bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 hover:text-white transition-colors"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                    />
                  </svg>
                </button>
              ) : (
                <button
                  onClick={handleSaveFighterClick}
                  className="p-2 rounded-lg bg-green-600/80 hover:bg-green-500 text-white transition-colors"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Card Body - Belt, Degree, Type Info */}
          {!isEditingFighter ? (
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-white/5">
              <button
                type="button"
                onClick={() => setIsGradOpen(true)}
                className="rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/40 hover:opacity-95 transition-opacity disabled:cursor-not-allowed"
                title="Editar graduação"
                disabled={!groupId || !canEditBelt}
              >
                <BeltVisual
                  color={resolvedBelt}
                  degree={beltDegree || 0}
                  size="sm"
                />
              </button>
              {type && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-slate-700/50 text-slate-300">
                  {t(`table.types.${type}`)}
                </span>
              )}
              {!belt && !type && (
                <span className="text-xs text-slate-500 italic">
                  {t('table.noInfoYet', 'Dados não preenchidos')}
                </span>
              )}
            </div>
          ) : (
            <div className="space-y-3 pt-3 border-t border-white/5">
              {/* Belt Select */}
              <div>
                <label className="block text-xs text-slate-400 mb-1.5">
                  {t('table.labels.belt')}
                </label>
                <select
                  value={fighterBelt}
                  onChange={handleBeltChange}
                  disabled={!canEditBelt}
                  className="w-full px-3 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-red-500/50 disabled:cursor-not-allowed"
                >
                  {!fighterBelt && (
                    <option disabled value="">
                      {t('table.selectBelt', 'Selecionar faixa')}
                    </option>
                  )}
                  <option value={Belt.Colors.White}>
                    ⚪ {t('belts.white')}
                  </option>
                  <option value={Belt.Colors.Blue}>🔵 {t('belts.blue')}</option>
                  <option value={Belt.Colors.Purple}>
                    🟣 {t('belts.purple')}
                  </option>
                  <option value={Belt.Colors.Brown}>
                    🟤 {t('belts.brown')}
                  </option>
                  <option value={Belt.Colors.Black}>
                    ⚫ {t('belts.black')}
                  </option>
                  <option value={Belt.Colors.RedBlack}>
                    🔴⚫ {t('belts.redBlack')}
                  </option>
                  <option value={Belt.Colors.RedWhite}>
                    🔴⚪ {t('belts.redWhite')}
                  </option>
                  <option value={Belt.Colors.Red}>🔴 {t('belts.red')}</option>
                </select>
              </div>

              {/* Degree & Type Row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1.5">
                    {t('table.labels.degree')}
                  </label>
                  <select
                    value={fighterBeltDegree}
                    onChange={handleBeltDegreeChange}
                    disabled={!canEditBelt}
                    className="w-full px-3 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-red-500/50 disabled:cursor-not-allowed"
                  >
                    <option value={0}>0°</option>
                    <option value={1}>1°</option>
                    <option value={2}>2°</option>
                    <option value={3}>3°</option>
                    <option value={4}>4°</option>
                    {fighterBelt === Belt.Colors.Black && (
                      <option value={5}>5°</option>
                    )}
                    {fighterBelt === Belt.Colors.Black && (
                      <option value={6}>6°</option>
                    )}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1.5">
                    {t('table.labels.gymRole')}
                  </label>
                  <select
                    value={fighterType}
                    onChange={handleTypeChange}
                    className="w-full px-3 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-red-500/50"
                  >
                    {!fighterType && (
                      <option disabled value="">
                        {t('table.selectRole', 'Selecionar')}
                      </option>
                    )}
                    <option value={FighterType.STUDENT}>
                      {t(`table.types.${FighterType.STUDENT}`)}
                    </option>
                    {(isInstructor || isMaster) && (
                      <option value={FighterType.HEAD_COACH}>
                        {t(`table.types.${FighterType.HEAD_COACH}`)}
                      </option>
                    )}
                    {(isInstructor || isMaster) && (
                      <option value={FighterType.PROFESSOR}>
                        {t(`table.types.${FighterType.PROFESSOR}`)}
                      </option>
                    )}
                    {isMaster && (
                      <option value={FighterType.MASTER}>
                        {t(`table.types.${FighterType.MASTER}`)}
                      </option>
                    )}
                    <option value={FighterType.OTHER}>
                      {t(`table.types.${FighterType.OTHER}`)}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default FighterRowConfirmationTable
